import { useMemo } from 'react'
import type { Table } from 'apache-arrow'
import type { BlastSource } from '../../../Model/Project/BlastSource.js'
import type { GroupModeKey } from '../../../Generated/GroupMode.ts'
import {
  fetchSpinMembers,
  loadOtuCube,
  loadOtuReads,
  loadSpinReads,
  loadSpinSummary,
  type SpinChoice
} from '../../../Repository/Visualization/OtuCubes.js'
import { useDispatch } from '../../../Flux/hooks.ts'
import { spinStatusSlice } from '../../../Flux/Slice/Spin/SpinStatusSlice.ts'
import {
  NO_OTUS,
  decodeOtuCube,
  decodeSpinSummary,
  groupOtuCube,
  indexOtuReads,
  indexSpinReads,
  spinSummaryGroups,
  type OtuCube,
  type OtuGroups,
  type OtuReads,
  type ScopeSpinSummary
} from './OtuCubeTable.logic.js'

/**
 * The rows the OTU table shows: the per-OTU cube grouped in the browser by [groupMode], or one row per SPIN of the
 * server's summary under [algorithm] ('' while the scope's algorithms are unknown, which loads nothing) as of
 * [atTime] ('' = Latest).
 */
export type GroupSourceChoice = { kind: 'classic'; groupMode: GroupModeKey } | { kind: 'spin'; algorithm: string; atTime: string }

/** One load of a {@link GroupSource}: its rows before grouping, and how to get their reads per sample. */
export type GroupLoad<D> = {
  data: D
  /** The OTUs the groups refer to; empty where the rows carry their own attributes. */
  cube: OtuCube
  /** The divisor of a representation computed from the reads; `null` where the rows bring their own. */
  scopeSamples: number | null
  /** How many rows were loaded, before grouping. */
  size: number
  topN: number | null
  /** The rows of the scope before the `topN` cut; `null` when the server did not say. */
  total: number | null
  /** Whether the server said it cut; `null` when it did not say. */
  truncated: boolean | null
  sources: BlastSource[] | null
  /** Names the reads {@link loadReads} gives, within the source's kind. */
  readsKey: string
  loadReads: () => Promise<Table>
  indexReads: (table: Table) => OtuReads
}

/** Where the OTU table's rows come from; `useOtuCubeTable` filters, sorts, pages and exports them. */
export type GroupSource<D = unknown> = {
  /** "Load all" and the reads cache are kept per kind, so one does not evict the other. */
  kind: 'classic' | 'spin'
  /** What {@link load} loads; a load under another key never shows under this one. */
  key: string
  unit: 'OTUs' | 'SPINs'
  /** Loads the rows, cut to the [topN] most abundant unless null. */
  load(topN: number | null): Promise<GroupLoad<D>>
  group(load: GroupLoad<D>): OtuGroups
  /** Fetches the member OTUs of row [rowId], most reads first; absent where the groups carry them. */
  fetchMembers?: (rowId: string) => Promise<string[]>
  /** The SPIN selection whose not yet clustered OTUs the result note counts. */
  unclustered: SpinChoice | null
}

export function classicGroupSource(scopeId: string, blastType: BlastSource, groupMode: GroupModeKey): GroupSource<OtuCube> {
  return {
    kind: 'classic',
    key: `${scopeId}:${blastType}`,
    unit: 'OTUs',
    unclustered: null,
    async load(topN) {
      const loaded = await loadOtuCube(scopeId, blastType, topN)
      const cube = decodeOtuCube(loaded.table)
      return {
        data: cube,
        cube,
        scopeSamples: loaded.scopeSamples,
        size: cube.size,
        topN,
        total: loaded.otuTotal,
        truncated: null,
        sources: loaded.sources ?? null,
        // The matrix is the scope's, whatever the blast source or cut.
        readsKey: scopeId,
        loadReads: () => loadOtuReads(scopeId),
        indexReads: (table) => indexOtuReads(table, cube)
      }
    },
    group: (load) => groupOtuCube(load.data, groupMode, load.scopeSamples)
  }
}

/** [onChanged] runs when the summary replaced a cached one, the scope's SPINs having changed since. */
export function spinGroupSource(
  scopeId: string,
  blastType: BlastSource,
  spin: SpinChoice,
  onChanged: () => void
): GroupSource<ScopeSpinSummary> {
  return {
    kind: 'spin',
    key: `${scopeId}:${blastType}:spin:${spin.algorithmType}:${spin.atTime}`,
    unit: 'SPINs',
    unclustered: spin,
    async load(topN) {
      const loaded = await loadSpinSummary(scopeId, blastType, spin, topN)
      if (loaded.replaced) {
        onChanged()
      }
      const summary = decodeSpinSummary(loaded.table)
      return {
        data: summary,
        cube: NO_OTUS,
        scopeSamples: null,
        size: summary.size,
        topN,
        total: loaded.spinTotal,
        truncated: loaded.truncated,
        sources: loaded.sources ?? null,
        readsKey: `${scopeId}:${spin.algorithmType}:${spin.atTime}:${topN ?? 'all'}`,
        loadReads: () => loadSpinReads(scopeId, spin, topN),
        indexReads: (table) => indexSpinReads(table, summary)
      }
    },
    group: (load) => spinSummaryGroups(load.data, spin.atTime),
    fetchMembers: (spinId) => fetchSpinMembers(scopeId, spinId, spin)
  }
}

/** The source of [choice] in [scopeId] under [blastType]; null while there is nothing to load. Stable while they are. */
export function useGroupSource(choice: GroupSourceChoice | null, scopeId: string | undefined, blastType: BlastSource): GroupSource | null {
  const dispatch = useDispatch()
  const groupMode = choice?.kind === 'classic' ? choice.groupMode : null
  const algorithm = choice?.kind === 'spin' ? choice.algorithm : null
  const atTime = choice?.kind === 'spin' ? choice.atTime : ''
  return useMemo((): GroupSource | null => {
    if (!scopeId) {
      return null
    }
    if (groupMode !== null) {
      return classicGroupSource(scopeId, blastType, groupMode)
    }
    if (!algorithm) {
      return null
    }
    const spin = { algorithmType: algorithm, atTime }
    return spinGroupSource(scopeId, blastType, spin, () => dispatch(spinStatusSlice.actions.spinDataChanged({ scopeId })))
  }, [scopeId, blastType, groupMode, algorithm, atTime, dispatch])
}
